import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { generateSpeech } from '../features/assistant/api/queries/useTextToSpeech';
import { useAudioStore } from '../features/assistant/stores/useAudioStore';
import { createSelectors } from '../utils/store';

import type { ChatApiResponse, FinancialRiskCalculator, PensionCalculator } from '../api/getChat';
import type { ChatMessage } from '../types/chat';

interface PersistedStore {
  isOnboarded: boolean;
}

interface SessionStore {
  sessionId: string | null;
  setSessionId: (sessionId: string) => void;
  setOnboarded: () => void;
  isSidebarOpen: boolean;
  setSidebarOpen: (isOpen: boolean) => void;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  audioState: 'idle' | 'speaking' | 'processing';
  setAudioState: (audioState: 'idle' | 'speaking' | 'processing') => void;
  sidebarContent: ChatApiResponse['slides'] | null;
  currentAudioUrl: string | null;
  currentVideoUrl: string | null;
  relatedQuestions: string[] | null;
  activeSlideIndex: number;
  setActiveSlideIndex: (index: number) => void;
  financialRiskCalculations: FinancialRiskCalculator | null;
  pensionCalculations: PensionCalculator | null;
  sendMessage: (userText: string) => Promise<void>;
  addAIResponse: (response: Pick<ChatApiResponse, 'response' | 'slides'>) => void;
  setRelatedQuestions: (questions: string[] | null) => void;
  playAudio: (url: string) => void;
  playVideo: (url: string) => void;
  clearMedia: () => void;
}

interface ChatState extends SessionStore, PersistedStore {}

export const useChatStore = createSelectors(
  create<ChatState, [['zustand/persist', PersistedStore]]>(
    persist(
      (set) => ({
        sessionId: null,
        messages: [],
        loading: false,
        error: null,
        currentAudioUrl: null,
        currentVideoUrl: null,
        sidebarContent: null,
        isOnboarded: false,
        isSidebarOpen: false,
        financialRiskCalculations: null,
        pensionCalculations: null,
        activeSlideIndex: 0,
        setActiveSlideIndex: (index: number) => {
          set({ activeSlideIndex: index });
        },
        relatedQuestions: null,
        audioState: 'idle',
        setAudioState: (audioState: 'idle' | 'speaking' | 'processing') => {
          set({ audioState });
        },
        setSidebarOpen: (isOpen: boolean) => {
          set({ isSidebarOpen: isOpen });
        },
        setOnboarded: () => {
          set({ isOnboarded: true });
        },
        setSessionId: (sessionId: string) => {
          set({ sessionId });
        },
        setRelatedQuestions: (questions: string[] | null) => {
          set({ relatedQuestions: questions });
        },
        addAIResponse: async (res) => {
          const { response, slides } = res;
          const aiMessage: ChatMessage = {
            id: crypto.randomUUID(),
            type: 'ai',
            content: response,
            timestamp: Date.now(),
          };

          const hasContent = slides && Object.keys(slides).length > 0;

          const { isMuted } = useAudioStore.getState();
          if (!isMuted) {
            const speech = await generateSpeech(response);

            useAudioStore.getState().playAudio(speech);
          }

          set((state) => ({
            messages: [...state.messages, aiMessage],
            currentAudioUrl: aiMessage.audioUrl || null,
            currentVideoUrl: aiMessage.videoUrl || null,
            isSidebarOpen: (!state.isSidebarOpen && hasContent) || state.isSidebarOpen,
            sidebarContent: hasContent ? slides : state.sidebarContent,

            loading: false,
            error: null,
          }));
        },
        sendMessage: async (userText: string) => {
          const userMessage: ChatMessage = {
            id: crypto.randomUUID(),
            type: 'user',
            content: userText,
            timestamp: Date.now(),
          };
          set((state) => ({
            messages: [...state.messages, userMessage],
            loading: true,
            error: null,
          }));
        },

        playAudio: (url: string) => {
          set({ currentAudioUrl: url });
        },

        playVideo: (url: string) => {
          set({ currentVideoUrl: url });
        },

        clearMedia: () => {
          set({ currentAudioUrl: null, currentVideoUrl: null });
        },
      }),

      {
        name: 'chat-storage',
        partialize: (state) => ({ isOnboarded: state.isOnboarded }),
      },
    ),
  ),
);

// dummy data
export const MESSAGES: ChatMessage[] = [
  { id: '1', type: 'ai', content: "Hi Tim, let's calculate your financial safety net." },
  { id: '2', type: 'user', content: "I'm 28 years old." },
  { id: '3', type: 'ai', content: 'Great! What are your personal fixed expenses per month?' },
  { id: '4', type: 'user', content: '€2.500' },
  { id: '5', type: 'ai', content: 'Thanks, and your annual income?' },
  { id: '6', type: 'user', content: '€80.000' },
];
